/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useSearchParams } from 'react-router-dom'
import { Form, Formik } from 'formik'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import moment from 'moment'
import ReactSelect from 'react-select'
import { omit, startCase } from 'lodash'
import { toast } from 'react-toastify'

import DataTable from 'components/atoms/DataTable'
import { PayerCrossoverIdDto } from 'store/types'
import {
  useAddLabPayerNameMutation,
  useAddLabPayerNoteMutation,
  useCreateLabPayerMutation,
  useGetLabPayerCrossoverIdsQuery,
  useGetLabPayerDmePayersQuery,
  useGetLabPayerHistoryQuery,
  useGetLabPayerListQuery,
  useGetLabPayerQuery,
  useRemoveLabPayerCrossoverIdMutation,
  useRemoveLabPayerNameMutation,
  useRemoveLabPayerNoteMutation,
  useUpdateLabPayerMutation,
  useUpdateLabPayerNoteMutation,
} from 'store/services/payer'
import {
  useDeletePayerChargeRuleMutation,
  useSavePayerChargeRuleMutation,
} from 'store/services/payerChargeRules'
import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  headingsPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  quotePlugin,
  toolbarPlugin,
  UndoRedo,
} from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from '../user/permissions'
import { TableWrapper } from 'components/atoms/TableWrapper'
import DashboardContent from 'components/lab/atoms/DashboardContent'
import ButtonNew from 'components/atoms/ButtonNew'
import Input from 'components/atoms/Input'
import { confirm } from 'components/molecules/Confirmation'
import { handleChange } from 'components/atoms/Upsert'
import { UpdatePayorConfirmation } from '../orderEdit/payor/UpdatePayorConfirmation'
import {
  ClaimSubmissionMethod,
  LabPayerBillingForm,
  LabPayerForm,
  LabPayerFormValues,
  PAYER_TYPE_LABELS,
  PayerType,
} from 'components/lab/orderEdit/payor/LabPayerForm'
import { payorValidation } from 'components/lab/orderEdit/payor/LabPayorDetailsModal'
import { useOptumPayerIdConfirmation } from 'components/lab/orderEdit/payor/useOptumPayerIdConfirmation'
import ChargeRuleForm from 'components/lab/payer/chargeRules/ChargeRuleForm'
import {
  ChargeConfigFormValues,
  chargeRuleErrorMessage,
  newFromConfig,
  sortConfigsForTable,
  StoredChargeConfig,
  toConfigFormValues,
  todayIso,
  toSaveRequest,
} from 'components/lab/payer/chargeRules/chargeRule'
import {
  ChargeConfigRow,
  chargeConfigColumns,
  ShowEndedCheckbox,
  toConfigRow,
  useShowEnded,
} from 'components/lab/payer/chargeRules/chargeConfigTable'
import { payerDisplayName } from 'components/lab/payer/payerDisplayName'
import { ResizableSplit } from 'components/lab/payer/ResizableSplit'

type CrossoverIdCell = { row: { original: PayerCrossoverIdDto } }

// How a saved payer id came to be on the payer: see PayerCrossoverIdSource.
const CROSSOVER_ID_SOURCE_LABELS: { [key: string]: string } = {
  AUTO_ID: 'Matched by payer ID',
  AUTO_NAME: 'Matched by name',
  MANUAL: 'Set from the exception queue',
  POSTED: 'Remembered from a posting',
}

const StyledTableWrapper = styled(TableWrapper)`
  max-height: calc(100vh - 230px);
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.6rem;
    div {
      span {
        font-size: 0.9rem;
      }
    }
  }
`

const PAGE_SIZE = 20
// Wide enough for the list's filters and columns; past it only whitespace grows.
const LIST_MAX_WIDTH = 820

const activeFilterOptions = [
  { value: undefined, label: 'Filter Active...' },
  { value: true, label: 'Active' },
  { value: false, label: 'Inactive' },
]
const payerIdFilterOptions = [
  { value: undefined, label: 'Filter Payor ID...' },
  { value: false, label: 'Missing Payor ID' },
  { value: true, label: 'Payor ID Present' },
]
// Every payer starts out In/Out of Network from the checkbox it replaced;
// filtering is how staff work through the list setting the other two.
const payerTypeFilterOptions = [
  { value: undefined, label: 'Filter Payor Type...' },
  ...Object.values(PayerType).map((value) => ({ value, label: PAYER_TYPE_LABELS[value] })),
]

// The filters sit above a table whose header is `position: sticky`, and a
// react-select menu defaults to z-index 1 — the same as that header, which
// comes later in the document and so painted over the open menu. Rendering the
// menu into the body takes it out of the argument entirely.
const filterMenuProps = {
  menuPortalTarget: document.body,
  styles: { menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 10 }) },
}

type Obj = { [key: string]: string }
const renderObject = (obj: Obj) =>
  Object.entries(obj).map(([key, value]) => (
    <div>
      <span css={{ fontWeight: '700', marginRight: 8 }}>{startCase(key)}:</span>
      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
    </div>
  ))

/**
 * A configuration of a payer not saved yet: it waits here, with an id of its
 * own, until the create call carries it.
 */
const toDraftConfig = (values: ChargeConfigFormValues): StoredChargeConfig => {
  const request = omit(toSaveRequest(values, 0), 'payerId')

  return {
    ...request,
    id: values.id ?? crypto.randomUUID(),
    billedAmount: request.billedAmount ?? null,
    contractRate: request.contractRate ?? null,
    contracted: request.contracted ?? null,
  }
}

const getDifference = (obj1: Obj, obj2: Obj) =>
  Object.fromEntries(
    Object.entries(obj2).filter(
      ([key, val]) => (key in obj1 && obj1[key] !== val) || !(key in obj1),
    ),
  )

const Payer = () => {
  const [values, setValues] = useState<{
    searchName: string
    searchId: string
    active?: boolean
    hasPayerId?: boolean
    payerType?: PayerType
  }>({
    searchName: '',
    searchId: '',
    active: true,
    hasPayerId: undefined,
    payerType: undefined,
  })
  const [currentPage, setCurrentPage] = useState(0)
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedPayor, setSelectedPayor] = useState<number | null>(null)
  const [showUpdatePayorConfirmation, setShowUpdatePayorConfirmation] = useState(false)
  const [updatedPayerValues, setUpdatedPayerValues] = useState<LabPayerFormValues>()
  const [newName, setNewName] = useState('')
  const [chargeForm, setChargeForm] = useState<ChargeConfigFormValues>(toConfigFormValues)
  const [showChargeForm, setShowChargeForm] = useState(false)
  const [draftCharges, setDraftCharges] = useState<StoredChargeConfig[]>([])
  const [showEndedCharges, setShowEndedCharges] = useShowEnded('lab.payer.charges.showEnded')
  const [editingNoteUuid, setEditingNoteUuid] = useState<string | null>(null)
  const [noteDraft, setNoteDraft] = useState('')
  const noteEditorRef = useRef(null)
  const { data: payers, isLoading } = useGetLabPayerListQuery({
    name: values.searchName,
    uPayerId: values.searchId,
    active: values.active,
    hasPayerId: values.hasPayerId,
    payerType: values.payerType,
    size: PAGE_SIZE,
    page: currentPage,
  })
  const { data: payer, isLoading: isLoadingPayer, refetch } = useGetLabPayerQuery(
    selectedPayor ? { id: selectedPayor.toString() } : skipToken,
  )
  const canEditInsurancePayers = useLabPermission(UserPermission.EDIT_INSURANCE_PAYERS) && !isLoadingPayer
  const { data: dmePayers, isLoading: isLoadingDmePayers } = useGetLabPayerDmePayersQuery(
    selectedPayor ? { id: selectedPayor.toString() } : skipToken,
  )
  const { data: payerHistory, isLoading: isLoadingPayerHistory } = useGetLabPayerHistoryQuery(
    selectedPayor ? { id: selectedPayor.toString() } : skipToken,
  )

  const [createLabPayer] = useCreateLabPayerMutation()
  const [updateLabPayer] = useUpdateLabPayerMutation()
  const [addLabPayerName] = useAddLabPayerNameMutation()
  const [removeLabPayerName] = useRemoveLabPayerNameMutation()
  const [removeLabPayerCrossoverId] = useRemoveLabPayerCrossoverIdMutation()
  const { data: crossoverIds } = useGetLabPayerCrossoverIdsQuery(Number(selectedPayor), {
    skip: selectedPayor === null || selectedPayor === 0,
  })
  const [addLabPayerNote] = useAddLabPayerNoteMutation()
  const [updateLabPayerNote] = useUpdateLabPayerNoteMutation()
  const [removeLabPayerNote] = useRemoveLabPayerNoteMutation()
  const [savePayerChargeRule] = useSavePayerChargeRuleMutation()
  const [deletePayerChargeRule] = useDeletePayerChargeRuleMutation()
  const confirmOptumPayerIds = useOptumPayerIdConfirmation()

  // Deep link from elsewhere (the Charge Rules screen links a rule back to its
  // payer). Selecting a payer is component state, so the id has to be lifted
  // out of the URL; it is then cleared so a later "back to list" is not undone
  // by a stale param.
  useEffect(() => {
    const linkedPayerId = Number(searchParams.get('payerId'))

    if (linkedPayerId) {
      setSelectedPayor(linkedPayerId)
      searchParams.delete('payerId')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    setCurrentPage(0)
  }, [values])

  useEffect(() => {
    setNewName('')
    setChargeForm(toConfigFormValues())
    setShowChargeForm(false)
    setDraftCharges([])
    setEditingNoteUuid(null)
    setNoteDraft('')
    noteEditorRef.current?.setMarkdown('')
  }, [selectedPayor])

  const initialValues: LabPayerFormValues = useMemo(
    () =>
      selectedPayor === 0
        ? {
            name: '',
            verificationLink: '',
            telephone: '',
            ourProviderId: '',
            payorId: '',
            addressStreetLine1: '',
            addressStreetLine2: '',
            addressCity: '',
            addressState: '',
            addressZip: '',
            active: false,
            payerType: PayerType.OUT_OF_NETWORK,
            outsourceBlocked: false,
            notes: '',
            claimFileIndicator: '',
            placeOfService: '',
            claimSubmissionMethod: ClaimSubmissionMethod.ELECTRONIC,
            providerTaxonomyCode: '',
            providerCommercialNumber: '',
            timelyFilingDays: '',
            optumPayerId: '',
            optumClaimsPayerId: '',
            isTestSmarterBilling: false,
          }
        : {
            name: payer?.name ?? payer?.names?.[0]?.name ?? '',
            verificationLink: payer?.verificationLink ?? '',
            telephone: payer?.telephone?.replace(/^\+1/, '') ?? '',
            ourProviderId: payer?.ourProviderId ?? '',
            uPayerId: payers?.items?.find((p) => p.id === selectedPayor)?.uPayerId ?? '',
            planId: payer?.planId || '',
            addressStreetLine1: payer?.addressStreetLine1 ?? '',
            addressStreetLine2: payer?.addressStreetLine2 ?? '',
            addressCity: payer?.addressCity ?? '',
            addressState: payer?.addressState ?? '',
            addressZip: payer?.addressZip ?? '',
            active: payer?.active ?? false,
            payerType: payer?.payerType ?? PayerType.OUT_OF_NETWORK,
            outsourceBlocked: payer?.outsourceBlocked ?? false,
            notes: payer?.notes ?? '',
            claimFileIndicator: payer?.claimFileIndicator ?? '',
            placeOfService: payer?.placeOfService ?? '',
            claimSubmissionMethod: payer?.claimSubmissionMethod ?? ClaimSubmissionMethod.ELECTRONIC,
            providerTaxonomyCode: payer?.providerTaxonomyCode ?? '',
            providerCommercialNumber: payer?.providerCommercialNumber ?? '',
            timelyFilingDays: payer?.timelyFilingDays ?? '',
            optumPayerId: payer?.optumPayerId ?? '',
            optumClaimsPayerId: payer?.optumClaimsPayerId ?? '',
            aiVerificationEnabled: payer?.aiVerificationEnabled ?? true,
            isTestSmarterBilling: payer?.isTestSmarterBilling ?? false,
          },
    [payer, payers?.items, selectedPayor],
  )

  const onSave = async (values: LabPayerFormValues & { updateNote: string }) => {
    const payload = {
      ...values,
      timelyFilingDays:
        values.timelyFilingDays === '' || values.timelyFilingDays == null
          ? undefined
          : Number(values.timelyFilingDays),
    }
    try {
      if (selectedPayor && selectedPayor > 0) {
        await updateLabPayer({ id: selectedPayor, ...payload }).unwrap()
        await refetch()
      } else if (selectedPayor === 0) {
        const createdPayer = await createLabPayer({ ...payload, charges: draftCharges }).unwrap()
        setSelectedPayor(createdPayer.id)
      }
      toast.success('Payor saved successfully')
    } catch (error) {
      console.log(JSON.stringify(error))
      toast.error(`Payor could not be saved. ${error.data.message}`)
    } finally {
      setShowUpdatePayorConfirmation(false)
    }
  }

  const closeChargeForm = () => {
    setShowChargeForm(false)
    setChargeForm(toConfigFormValues())
  }

  const openChargeForm = (values: ChargeConfigFormValues) => {
    setChargeForm(values)
    setShowChargeForm(true)
  }

  const saveCharge = async () => {
    if (selectedPayor === null) {
      return
    }

    if (selectedPayor > 0) {
      // The dedicated command saves one configuration and checks it against
      // the payer's whole set, so this screen never sends a whole payer.
      try {
        await savePayerChargeRule(toSaveRequest(chargeForm, selectedPayor)).unwrap()
      } catch (error) {
        toast.error(chargeRuleErrorMessage(error, 'The charge could not be saved.'))
        return
      }
    } else {
      // The new-payer form has no payer to address a command to yet; its
      // configurations ride along with the create call, which checks them.
      const draft = toDraftConfig(chargeForm)

      setDraftCharges((previous) =>
        previous.some((config) => config.id === draft.id)
          ? previous.map((config) => (config.id === draft.id ? draft : config))
          : [...previous, draft],
      )
    }

    closeChargeForm()
  }

  const deleteCharge = async (config: StoredChargeConfig) => {
    if (selectedPayor && selectedPayor > 0) {
      try {
        await deletePayerChargeRule({ payerId: selectedPayor, id: config.id }).unwrap()
      } catch (error) {
        toast.error(chargeRuleErrorMessage(error, 'The charge could not be deleted.'))
      }
    } else if (selectedPayor === 0) {
      setDraftCharges((previous) => previous.filter((draft) => draft.id !== config.id))
    }
  }

  const payerData = useMemo(
    () => ({
      currentPage: payers?.currentPage ?? 0,
      items:
        payers?.items?.map((p) => ({
          ...p,
          name: payerDisplayName(p),
        })) ?? [],
      totalItems: payers?.totalItems ?? 0,
      totalPages: payers?.totalPages ?? 0,
    }),
    [payers],
  )

  const currentCharges: StoredChargeConfig[] = useMemo(
    () => (selectedPayor === 0 ? draftCharges : payer?.charges || []),
    [draftCharges, payer?.charges, selectedPayor],
  )
  // A configuration that leaves its contracted flag to the payer follows the
  // payer's in-network type, as reports do.
  const payerInNetwork = payer ? payer.payerType === PayerType.IN_NETWORK : undefined
  const currentNames = useMemo(
    () => (selectedPayor === 0 ? [] : payer?.names || []),
    [payer?.names, selectedPayor],
  )
  const currentDmePayers = useMemo(
    () => (selectedPayor === 0 ? [] : dmePayers || []),
    [dmePayers, selectedPayor],
  )
  const currentPayerHistory = useMemo(
    () => (selectedPayor === 0 ? [] : payerHistory || []),
    [payerHistory, selectedPayor],
  )
  const currentPayerNotes = useMemo(
    () => (selectedPayor === 0 ? [] : payer?.payerNotes || []),
    [payer?.payerNotes, selectedPayor],
  )

  const dmeTableData = useMemo(
    () => ({
      currentPage: 0,
      items:
        currentDmePayers?.map((p) => ({
          id: p.id,
          name: p.name,
        })) ?? [],
      totalItems: currentDmePayers?.length ?? 0,
      totalPages: 1,
    }),
    [currentDmePayers],
  )

  // Each row's end, status and fallback amount are read against the whole set,
  // ended rows included, before the ended ones are hidden.
  const chargesTableData = useMemo(() => {
    const today = todayIso()
    const items = sortConfigsForTable(currentCharges)
      .map((config) => toConfigRow(currentCharges, config, today))
      .filter((row) => showEndedCharges || row.status !== 'ENDED')

    return { currentPage: 0, items, totalItems: items.length, totalPages: 1 }
  }, [currentCharges, showEndedCharges])

  const chargeColumns = chargeConfigColumns<ChargeConfigRow>({
    payerInNetwork,
    actions: canEditInsurancePayers
      ? {
          onEdit: (row) => openChargeForm(toConfigFormValues(row)),
          onNewFrom: (row) => openChargeForm(newFromConfig(row, todayIso())),
          onDelete: deleteCharge,
          configsOf: () => currentCharges,
        }
      : undefined,
  })

  const historyData = useMemo(
    () => ({
      currentPage: 0,
      items:
        currentPayerHistory
          ?.filter((p) => p.eventName === 'PayerUpdatedEvent')
          ?.sort((a, b) => (a.timestamp > b.timestamp ? -1 : 1))
          ?.map((p) => ({
            timestamp: moment(p.timestamp).format('MM/DD/YYYY hh:mm A'),
            user: `${p.user?.firstName ?? ''} ${p.user?.lastName ?? ''}`,
            data: p.dto.charges
              ? {
                  ...p.dto,
                  charges: JSON.stringify(p.dto.charges, null, 2),
                }
              : p.dto,
          })) ?? [],
      totalItems: currentPayerHistory?.length ?? 0,
      totalPages: 1,
    }),
    [currentPayerHistory],
  )

  const header = [
    {
      accessor: 'id',
      Header: 'ID',
    },
    {
      accessor: 'uPayerId',
      Header: 'Payor ID',
      Cell: ({ row }) => <>{row.original.uPayerId ?? '-'}</>,
    },
    { accessor: 'name', Header: 'Name' },
    {
      accessor: 'payerType',
      Header: 'Payor Type',
      Cell: ({ row }) => <>{PAYER_TYPE_LABELS[row.original.payerType as PayerType] ?? '-'}</>,
    },
    {
      accessor: 'active',
      Header: 'Active',
      Cell: ({ row }) => <>{row.original.active ? 'Yes' : 'No'}</>,
    },
  ]

  const historyHeader = [
    { accessor: 'timestamp', Header: 'Date' },
    { accessor: 'user', Header: 'User' },
    {
      submission: 'submission',
      Header: 'Submission',
      Cell: ({ row, rows }) => {
        const currRowId = row.id
        const prevRow = rows[Number(currRowId) + 1]
        if (!prevRow) {
          return renderObject(row.original.data)
        }
        return renderObject(getDifference(prevRow.original.data, row.original.data))
      },
    },
  ]

  return (
    <DashboardContent
      title="Payors"
      icon="fa fas fa-shield-alt"
      content={
        <ResizableSplit storageKey="lab_payer_list_width" maxLeftWidth={LIST_MAX_WIDTH}>
          <>
            <div className="card card-primary">
              <form>
                <div className="card-header d-flex">
                  <div className="input-group mr-1" css={{ width: 'unset', flex: 1 }}>
                    <Input
                      autoFocus={true}
                      value={values.searchName}
                      placeholder="Search Name..."
                      name="searchName"
                      parentDiv={null}
                      handleChange={(event) => {
                        handleChange(event, values, setValues)
                      }}
                    />
                  </div>
                  <div className="input-group mr-1" css={{ width: 'unset', flex: 1 }}>
                    <Input
                      autoFocus={true}
                      value={values.searchId}
                      placeholder="Search Payor ID..."
                      name="searchId"
                      parentDiv={null}
                      handleChange={(event) => {
                        handleChange(event, values, setValues)
                      }}
                    />
                  </div>
                  <div css={{ flex: 1, maxWidth: 190 }} className="mr-1">
                    <ReactSelect
                      {...filterMenuProps}
                      className="basic-single w-100 mr-3"
                      css={{ '& .select__control': { height: 34, minHeight: 'unset' } }}
                      classNamePrefix="select"
                      isClearable
                      placeholder="Filter Active"
                      name="active"
                      onChange={(item) => setValues({ ...values, active: item?.value })}
                      options={activeFilterOptions}
                      value={activeFilterOptions.find((o) => o.value === values.active)}
                    />
                  </div>
                  <div css={{ flex: 1, maxWidth: 190 }} className="mr-1">
                    <ReactSelect
                      {...filterMenuProps}
                      className="basic-single w-100 mr-3"
                      css={{ '& .select__control': { height: 34, minHeight: 'unset' } }}
                      classNamePrefix="select"
                      isClearable
                      placeholder="Filter Payer ID"
                      name="hasPayerId"
                      onChange={(item) => setValues({ ...values, hasPayerId: item?.value })}
                      options={payerIdFilterOptions}
                      value={payerIdFilterOptions.find((o) => o.value === values.hasPayerId)}
                    />
                  </div>
                  <div css={{ flex: 1, maxWidth: 190 }} className="mr-1">
                    <ReactSelect
                      {...filterMenuProps}
                      className="basic-single w-100 mr-3"
                      css={{ '& .select__control': { height: 34, minHeight: 'unset' } }}
                      classNamePrefix="select"
                      isClearable
                      placeholder="Filter Payor Type"
                      name="payerType"
                      onChange={(item) => setValues({ ...values, payerType: item?.value })}
                      options={payerTypeFilterOptions}
                      value={payerTypeFilterOptions.find((o) => o.value === values.payerType)}
                    />
                  </div>
                  <div className="text-right" css={{ minWidth: 70 }}>
                    {canEditInsurancePayers && <ButtonNew onClick={() => setSelectedPayor(0)} />}
                  </div>
                </div>
                <div className="card-body align-items-center">
                  <StyledTableWrapper className="card-body row">
                    <DataTable
                      tableKey={`lab_payer_payors`}
                      data={payerData}
                      status={isLoading}
                      tableColumns={header}
                      pageSize={PAGE_SIZE}
                      setCurrentPage={setCurrentPage}
                      currentPage={currentPage}
                      setSortOptions={() => {}}
                      entityName="payors"
                      highlightRowId={selectedPayor}
                      onRowClick={(row) => setSelectedPayor(row.original.id)}
                    />
                  </StyledTableWrapper>
                </div>
              </form>
            </div>
          </>
          <>
            <div
              className="card card-primary"
              // css={{ overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}
            >
              <div className="card-header" css={{ backgroundColor: '#4a89b3 !important' }}>
                <h3 className="card-title">Payor Details</h3>
              </div>
              <div
                className="card-body"
                css={{
                  '& ul': {
                    borderBottom: '1px solid',
                    '& li': {
                      '& button': {
                        minWidth: '100px',
                        marginBottom: '0 !important',
                        borderBottom: '1px solid',
                        borderBottomStyle: 'none !important',
                        '& .active': { borderBottomStyle: 'unset' },
                      },
                    },
                  },
                }}>
                <Formik
                  enableReinitialize={true}
                  validationSchema={payorValidation(!!initialValues?.uPayerId)}
                  initialValues={initialValues}
                  onSubmit={async (values) => {
                    if (!(await confirmOptumPayerIds(values, initialValues))) {
                      return
                    }

                    setUpdatedPayerValues(values)
                    if (selectedPayor === 0) {
                      onSave(values)
                    } else {
                      setShowUpdatePayorConfirmation(true)
                    }
                  }}>
                  {({ isValid }) => (
                    <Form
                      onKeyDown={(e) => {
                        // The form now wraps every tab, so block Enter (except in
                        // textareas) from implicitly submitting the payor form.
                        if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                          e.preventDefault()
                        }
                      }}>
                      <Tabs defaultActiveKey="form" className="mb-3">
                        <Tab eventKey="form" title="Details">
                          {selectedPayor !== null ? (
                            <>
                              <fieldset disabled={!canEditInsurancePayers}>
                                <LabPayerForm />
                              </fieldset>
                              <div className="d-flex justify-content-end mt-2">
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => setSelectedPayor(null)}>
                                  Cancel
                                </Button>
                                {canEditInsurancePayers && (
                                  <Button type="submit" disabled={!isValid} className="ml-3">
                                    Confirm
                                  </Button>
                                )}
                              </div>
                            </>
                          ) : (
                            <div>Select a payor to view details</div>
                          )}
                        </Tab>
                        <Tab eventKey="billing" title="Billing">
                          {selectedPayor !== null ? (
                            <>
                              <fieldset disabled={!canEditInsurancePayers}>
                                <LabPayerBillingForm />
                              </fieldset>
                              <div className="d-flex justify-content-end mt-2">
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => setSelectedPayor(null)}>
                                  Cancel
                                </Button>
                                {canEditInsurancePayers && (
                                  <Button type="submit" disabled={!isValid} className="ml-3">
                                    Confirm
                                  </Button>
                                )}
                              </div>
                            </>
                          ) : (
                            <div>Select a payor to view details</div>
                          )}
                        </Tab>
                        <Tab
                    eventKey="charges"
                    title={`Charges (${chargesTableData.items.length})`}>
                    {canEditInsurancePayers && (
                      <Modal size="xl" show={showChargeForm} onHide={closeChargeForm}>
                        <Modal.Body className="p-0">
                          <ChargeRuleForm
                            values={chargeForm}
                            setValues={setChargeForm}
                            configs={currentCharges}
                            onSave={saveCharge}
                            onCancel={closeChargeForm}
                            className="mb-0 border-0"
                          />
                        </Modal.Body>
                      </Modal>
                    )}
                    <div className="d-flex justify-content-end align-items-center mt-2">
                      <ShowEndedCheckbox
                        checked={showEndedCharges}
                        onChange={setShowEndedCharges}
                      />
                      {canEditInsurancePayers && (
                        <Button
                          variant="outline-primary"
                          className="ml-3"
                          onClick={() => openChargeForm(toConfigFormValues())}>
                          Add Charge Rule
                        </Button>
                      )}
                    </div>
                    <StyledTableWrapper className="card-body row">
                      <DataTable
                        data={chargesTableData}
                        tableKey={`lab_payer_charges`}
                        status={false}
                        tableColumns={chargeColumns}
                        pageSize={50}
                        setCurrentPage={() => {}}
                        setPageSize={() => {}}
                        setSortOptions={() => {}}
                        entityName="charge data"
                      />
                    </StyledTableWrapper>
                  </Tab>
                  <Tab
                    eventKey="dmePayors"
                    title={`DME Payors (${dmeTableData?.items?.length ?? 0})`}>
                    <StyledTableWrapper className="card-body row">
                      <DataTable
                        tableKey={`lab_payer_dme_payors`}
                        data={dmeTableData}
                        status={isLoadingDmePayers}
                        tableColumns={[
                          {
                            accessor: 'id',
                            Header: 'ID',
                          },
                          { accessor: 'name', Header: 'Name' },
                        ]}
                        pageSize={50}
                        setCurrentPage={() => {}}
                        setSortOptions={() => {}}
                        entityName="payors"
                      />
                    </StyledTableWrapper>
                  </Tab>
                  <Tab eventKey="history" title={`History (${historyData?.items?.length ?? 0})`}>
                    <StyledTableWrapper className="card-body row">
                      <DataTable
                        tableKey={`lab_payer_update_history`}
                        data={historyData}
                        status={isLoadingPayerHistory}
                        tableColumns={historyHeader}
                        pageSize={50}
                        setCurrentPage={() => {}}
                        setSortOptions={() => {}}
                        entityName="update history"
                      />
                    </StyledTableWrapper>
                  </Tab>
                  <Tab eventKey="names" title={`Other Names (${currentNames.length})`}>
                    {canEditInsurancePayers && selectedPayor !== null && selectedPayor > 0 && (
                      <div
                        className="input-group mr-1 d-flex"
                        css={{ width: 'unset', flex: 1, marginBottom: 8 }}>
                        <Input
                          autoFocus={true}
                          value={newName}
                          placeholder="New Name..."
                          name="newName"
                          parentDiv={null}
                          handleChange={(event) => setNewName(event.target.value)}
                        />
                        <Button
                          disabled={!newName}
                          variant="primary"
                          css={{ marginLeft: 8, minWidth: 100 }}
                          onClick={() =>
                            addLabPayerName({
                              payerId: payer!.id,
                              name: newName,
                            })
                              .unwrap()
                              .then(() => setNewName(''))
                          }>
                          Add
                        </Button>
                      </div>
                    )}
                    <StyledTableWrapper className="card-body row">
                      <DataTable
                        tableKey={`lab_payer_other_names`}
                        data={{
                          currentPage: 0,
                          items: currentNames,
                          totalItems: currentNames.length,
                          totalPages: 1,
                        }}
                        status={isLoadingPayer}
                        tableColumns={[
                          { accessor: 'name', Header: 'Name' },
                          {
                            accessor: 'active',
                            Header: 'Active',
                            Cell: ({ row }) => (row.original.active ? 'Yes' : 'No'),
                          },
                          ...(canEditInsurancePayers && selectedPayor !== null && selectedPayor > 0
                            ? [
                                {
                                  accessor: 'Delete',
                                  Cell: ({ row }) => (
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() =>
                                        confirm('Name')
                                          .then(() =>
                                            removeLabPayerName({
                                              payerId: payer!.id,
                                              uuid: row.original.uuid,
                                            }),
                                          )
                                          .catch(() => {})
                                      }>
                                      Delete
                                    </Button>
                                  ),
                                },
                              ]
                            : []),
                        ]}
                        pageSize={50}
                        setCurrentPage={() => {}}
                        setSortOptions={() => {}}
                        entityName="Other Names"
                      />
                    </StyledTableWrapper>
                  </Tab>
                  <Tab
                    eventKey="crossoverIds"
                    title={`Saved payer IDs (${selectedPayor ? (crossoverIds ?? []).length : 0})`}>
                    <div className="textGray" css={{ fontSize: 12, marginBottom: 8 }}>
                      Ids remittances have named this payer by that are not on its record: the id a
                      remittance came under and a biller posted to this payer, and the ids other payers
                      forward claims to it under (835 NM1*TT). Learned when such a remittance posts, or
                      set from the ERA exception queue. A remittance under one of these ids posts on its
                      own; delete a wrong one and the next remittance under it will ask again.
                    </div>
                    <StyledTableWrapper className="card-body row">
                      <DataTable
                        tableKey={`lab_payer_crossover_ids`}
                        data={{
                          currentPage: 0,
                          items: selectedPayor ? crossoverIds ?? [] : [],
                          totalItems: selectedPayor ? (crossoverIds ?? []).length : 0,
                        }}
                        status={isLoadingPayer}
                        tableColumns={[
                          { accessor: 'value', Header: 'ID' },
                          {
                            accessor: 'idType',
                            Header: 'Type',
                            Cell: ({ row }: CrossoverIdCell) =>
                              row.original.idType === 'COBA' ? 'Medicare COBA' : 'Payer ID',
                          },
                          {
                            accessor: 'carrierName',
                            Header: 'Named as',
                            Cell: ({ row }: CrossoverIdCell) => <>{row.original.carrierName ?? '-'}</>,
                          },
                          {
                            accessor: 'source',
                            Header: 'Source',
                            Cell: ({ row }: CrossoverIdCell) =>
                              CROSSOVER_ID_SOURCE_LABELS[row.original.source] ?? row.original.source,
                          },
                          {
                            accessor: 'createdAt',
                            Header: 'Since',
                            Cell: ({ row }: CrossoverIdCell) => moment(row.original.createdAt).format('MM/DD/YYYY'),
                          },
                          ...(canEditInsurancePayers && selectedPayor !== null && selectedPayor > 0
                            ? [
                                {
                                  accessor: 'Delete',
                                  Cell: ({ row }: CrossoverIdCell) => (
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() =>
                                        confirm('Crossover ID')
                                          .then(() =>
                                            removeLabPayerCrossoverId({
                                              payerId: payer!.id,
                                              id: row.original.id,
                                            }),
                                          )
                                          .catch(() => {})
                                      }>
                                      Delete
                                    </Button>
                                  ),
                                },
                              ]
                            : []),
                        ]}
                        pageSize={50}
                        setCurrentPage={() => {}}
                        setPageSize={() => {}}
                        setSortOptions={() => {}}
                        entityName="saved payer ids"
                      />
                    </StyledTableWrapper>
                  </Tab>
                  {currentPayerNotes.length > 1 && (
                    <Tab eventKey="notes" title="Other Notes">
                      <StyledTableWrapper className="card-body row">
                        <DataTable
                          tableKey={`lab_payer_payors`}
                          data={{
                            currentPage: 0,
                            items: currentPayerNotes,
                            totalItems: currentPayerNotes.length,
                            totalPages: 1,
                          }}
                          tableColumns={[
                            { accessor: 'note', Header: 'Note' },
                            {
                              accessor: 'created',
                              Header: 'Created',
                              Cell: ({ row }) =>
                                moment(row.original.createdAt).format('MM/DD/YYYY'),
                            },
                          ]}
                          pageSize={50}
                          setCurrentPage={() => {}}
                          setSortOptions={() => {}}
                          entityName="update history"
                        />
                      </StyledTableWrapper>
                    </Tab>
                  )}
                  <Tab
                    eventKey="payerNotes"
                    title={`Notes (${currentPayerNotes.length})`}>
                    {selectedPayor !== null && selectedPayor > 0 ? (
                      <div>
                        {canEditInsurancePayers && (
                          <div
                            className="border rounded p-3 mb-3"
                            css={{ background: '#f8fbfd' }}>
                            <div className="mb-2">
                              <strong>{editingNoteUuid ? 'Edit Note' : 'Add Note'}</strong>
                            </div>
                            <div
                              css={{
                                '& .mdxeditor': {
                                  border: '1px solid #ccc',
                                  borderRadius: '5px',
                                  background: '#fff',
                                },
                              }}>
                              <MDXEditor
                                ref={noteEditorRef}
                                contentEditableClassName="markdown-editor"
                                markdown={noteDraft}
                                plugins={[
                                  toolbarPlugin({
                                    toolbarContents: () => (
                                      <>
                                        <UndoRedo />
                                        <BlockTypeSelect />
                                        <BoldItalicUnderlineToggles />
                                        <ListsToggle />
                                        <CreateLink />
                                      </>
                                    ),
                                  }),
                                  headingsPlugin(),
                                  listsPlugin(),
                                  quotePlugin(),
                                  linkPlugin(),
                                  linkDialogPlugin(),
                                ]}
                                onChange={(value) => setNoteDraft(value)}
                              />
                            </div>
                            <div className="d-flex justify-content-end mt-2">
                              {editingNoteUuid && (
                                <Button
                                  variant="outline-secondary"
                                  className="mr-2"
                                  onClick={() => {
                                    setEditingNoteUuid(null)
                                    setNoteDraft('')
                                    noteEditorRef.current?.setMarkdown('')
                                  }}>
                                  Cancel
                                </Button>
                              )}
                              <Button
                                variant="primary"
                                disabled={!noteDraft.trim()}
                                onClick={async () => {
                                  if (editingNoteUuid) {
                                    await updateLabPayerNote({
                                      payerId: selectedPayor,
                                      noteUuid: editingNoteUuid,
                                      note: noteDraft,
                                    })
                                  } else {
                                    await addLabPayerNote({
                                      payerId: selectedPayor,
                                      note: noteDraft,
                                      type: 'VERIFICATION',
                                    })
                                  }
                                  setEditingNoteUuid(null)
                                  setNoteDraft('')
                                  noteEditorRef.current?.setMarkdown('')
                                }}>
                                {editingNoteUuid ? 'Save Changes' : 'Add Note'}
                              </Button>
                            </div>
                          </div>
                        )}
                        <div>
                          {[...currentPayerNotes]
                            .sort((a, b) =>
                              new Date(b.createdAt as string).getTime() -
                              new Date(a.createdAt as string).getTime(),
                            )
                            .map((note) => (
                              <div
                                key={note.uuid}
                                className="border rounded p-3 mb-2"
                                css={{ background: '#fff' }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <small className="text-muted">
                                    {moment(note.createdAt as string).format('MM/DD/YYYY hh:mm A')}
                                    {note.updatedAt && note.updatedAt !== note.createdAt && (
                                      <span className="ml-2">
                                        (edited {moment(note.updatedAt as string).format('MM/DD/YYYY hh:mm A')})
                                      </span>
                                    )}
                                  </small>
                                  {canEditInsurancePayers && (
                                    <div>
                                      <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 mr-2"
                                        onClick={() => {
                                          setEditingNoteUuid(note.uuid)
                                          setNoteDraft(note.note)
                                          noteEditorRef.current?.setMarkdown(note.note)
                                        }}>
                                        Edit
                                      </Button>
                                      <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 text-danger"
                                        onClick={() =>
                                          confirm('note')
                                            .then(() =>
                                              removeLabPayerNote({
                                                payerId: selectedPayor,
                                                noteUuid: note.uuid,
                                              }),
                                            )
                                            .catch(() => {})
                                        }>
                                        Delete
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                <div
                                  css={{
                                    '& .mdxeditor': {
                                      border: 'none',
                                      padding: 0,
                                      '& ._toolbarRoot_uazmk_1': { display: 'none' },
                                    },
                                  }}>
                                  <MDXEditor
                                    key={`${note.uuid}-${note.updatedAt}`}
                                    readOnly
                                    contentEditableClassName="markdown-editor"
                                    markdown={note.note ?? ''}
                                    plugins={[
                                      headingsPlugin(),
                                      listsPlugin(),
                                      quotePlugin(),
                                      linkPlugin(),
                                    ]}
                                  />
                                </div>
                              </div>
                            ))}
                          {currentPayerNotes.length === 0 && (
                            <div className="text-muted p-2">No notes yet.</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted p-2">Select a payor to view notes.</div>
                    )}
                  </Tab>
                	  </Tabs>
					  <UpdatePayorConfirmation
						open={showUpdatePayorConfirmation}
						onClose={() => setShowUpdatePayorConfirmation(false)}
						onSubmit={(updateNote: string) => {
                      	onSave({ ...updatedPayerValues, updateNote })
                      }}
                	  />
              		</Form>
            	  )}
          		</Formik>
        	  </div>
      	    </div>
    	  </>
  	    </ResizableSplit>
	  }
	  breadcrumb={[{ label: 'Payors' }]}
    />
  )
}

export default Payer
